import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Plus, ShoppingBag, ArrowLeft, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { formatINR } from "@/lib/format";
import { Field, TextInput, SelectInput, ErrorBox, btnPrimary, btnSecondary } from "@/components/common/Form";
import { EmptyState, LoadingState } from "@/components/common/States";

const EMPTY = { address_type: "HOME", full_name: "", phone: "", address_line_1: "", address_line_2: "", landmark: "", city: "", district: "", state: "", country: "India", postal_code: "", is_default: false };
const TYPES = [["HOME", "Home"], ["OFFICE", "Office"], ["SHIPPING", "Shipping"], ["BUSINESS", "Business"]];
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export default function Checkout() {
  const { refreshCart } = useStore();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState(null);
  const [selected, setSelected] = useState(null);
  const [review, setReview] = useState(null);
  const [adding, setAdding] = useState(null);
  const [error, setError] = useState("");
  const [placing, setPlacing] = useState(false);
  const [idemKey] = useState(newKey);
  const [payCfg, setPayCfg] = useState(null);
  useEffect(() => { api.get("/payments/config").then((r) => setPayCfg(r.data)).catch(() => {}); }, []);

  const loadAddresses = useCallback(() => api.get("/addresses").then((r) => {
    setAddresses(r.data);
    setSelected((s) => s || r.data.find((a) => a.is_default)?.id || r.data[0]?.id || null);
  }).catch(() => setAddresses([])), []);
  useEffect(() => { loadAddresses(); }, [loadAddresses]);

  useEffect(() => {
    if (!selected) { setReview(null); return undefined; }
    let cancelled = false;
    setReview(null);
    api.post("/checkout/validate", { address_id: selected }).then((r) => { if (!cancelled) setReview(r.data); })
      .catch((err) => { if (!cancelled) setReview({ error: formatApiError(err.response?.data?.detail) }); });
    return () => { cancelled = true; };
  }, [selected]);

  const saveAddress = async (e) => {
    e.preventDefault(); setError("");
    try {
      const r = await api.post("/addresses", adding);
      setAdding(null); await loadAddresses(); setSelected(r.data.id); toast.success("Address added");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const placeOrder = async () => {
    if (placing || !selected) return;
    setPlacing(true);
    try {
      const r = await api.post("/orders", { address_id: selected, idempotency_key: idemKey });
      await refreshCart();
      navigate(`/orders/confirmation/${r.data.order_number}`, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not place order");
      setPlacing(false);
      api.post("/checkout/validate", { address_id: selected }).then((res) => setReview(res.data)).catch(() => {});
    }
  };

  if (addresses === null) return <LoadingState label="Preparing checkout..." />;
  const set = (k) => (e) => setAdding({ ...adding, [k]: e.target.value });

  if (review && !review.error && review.vendors.length === 0 && review.issues.length === 0) {
    return <div className="max-w-3xl mx-auto px-4 py-16"><EmptyState title="Your cart is empty" message="Add products before checking out." action={<Link to="/shop" className={btnPrimary}>Go to shop</Link>} /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="checkout-page">
      <Link to="/cart" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-secondary mb-4" data-testid="checkout-back"><ArrowLeft size={14} /> Back to cart</Link>
      <h1 className="text-3xl font-serif font-bold text-stone-900 mb-6">Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-6">
          <section className="rounded-xl border border-border bg-card p-5" data-testid="checkout-address-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-lg font-bold text-stone-900 inline-flex items-center gap-2"><MapPin size={18} className="text-secondary" /> Delivery address</h2>
              {!adding && <button data-testid="checkout-add-address" onClick={() => setAdding({ ...EMPTY })} className={btnSecondary}><Plus size={14} /> New address</button>}
            </div>
            {adding && (
              <form onSubmit={saveAddress} className="grid sm:grid-cols-2 gap-4 mb-5 rounded-xl bg-muted/60 p-4" data-testid="checkout-address-form">
                <div className="sm:col-span-2"><ErrorBox message={error} testId="checkout-address-error" /></div>
                <Field label="Address type"><SelectInput testId="co-address-type" value={adding.address_type} onChange={set("address_type")} options={TYPES} /></Field>
                <Field label="Full name *"><TextInput testId="co-address-full-name" required value={adding.full_name} onChange={set("full_name")} /></Field>
                <Field label="Mobile *"><TextInput testId="co-address-phone" required value={adding.phone} onChange={set("phone")} /></Field>
                <Field label="PIN code *"><TextInput testId="co-address-postal" required value={adding.postal_code} onChange={set("postal_code")} /></Field>
                <Field label="Address line 1 *" className="sm:col-span-2"><TextInput testId="co-address-line1" required value={adding.address_line_1} onChange={set("address_line_1")} /></Field>
                <Field label="Address line 2" className="sm:col-span-2"><TextInput testId="co-address-line2" value={adding.address_line_2} onChange={set("address_line_2")} /></Field>
                <Field label="City *"><TextInput testId="co-address-city" required value={adding.city} onChange={set("city")} /></Field>
                <Field label="State *"><TextInput testId="co-address-state" required value={adding.state} onChange={set("state")} /></Field>
                <Field label="Country *"><TextInput testId="co-address-country" required value={adding.country} onChange={set("country")} /></Field>
                <div className="sm:col-span-2 flex gap-2">
                  <button data-testid="co-address-save" className={btnPrimary}>Save & use this address</button>
                  <button type="button" onClick={() => { setAdding(null); setError(""); }} data-testid="co-address-cancel" className={btnSecondary}>Cancel</button>
                </div>
              </form>
            )}
            {addresses.length === 0 && !adding ? (
              <p className="text-sm text-stone-500" data-testid="checkout-no-address">No saved address yet. Add a delivery address to continue.</p>
            ) : (
              <ul className="grid sm:grid-cols-2 gap-3" data-testid="checkout-address-list">
                {addresses.map((a) => (
                  <li key={a.id}>
                    <button type="button" onClick={() => setSelected(a.id)} data-testid={`checkout-address-${a.id}`}
                      className={`w-full text-left rounded-xl border p-4 text-sm transition-colors ${selected === a.id ? "border-primary bg-accent/40 ring-2 ring-primary/30" : "border-border hover:border-primary/50"}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-semibold tracking-wide text-secondary">{a.address_type}</span>
                        {selected === a.id && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary" data-testid="checkout-address-selected"><Check size={12} /> Delivering here</span>}
                      </div>
                      <div className="font-semibold text-stone-900">{a.full_name} · {a.phone}</div>
                      <div className="text-stone-600">{a.address_line_1}{a.address_line_2 && `, ${a.address_line_2}`}</div>
                      <div className="text-stone-600">{a.city}, {a.state} {a.postal_code}, {a.country}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/account/addresses" className="mt-3 inline-block text-xs text-stone-500 hover:text-secondary" data-testid="checkout-manage-addresses">Manage addresses</Link>
          </section>

          <section className="rounded-xl border border-border bg-card p-5" data-testid="checkout-review-section">
            <h2 className="font-serif text-lg font-bold text-stone-900 mb-4 inline-flex items-center gap-2"><ShoppingBag size={18} className="text-secondary" /> Order review</h2>
            {!selected && <p className="text-sm text-stone-500">Select a delivery address to review your order.</p>}
            {selected && !review && <LoadingState label="Validating cart..." />}
            {review?.error && <ErrorBox message={review.error} testId="checkout-review-error" />}
            {review && !review.error && (
              <div className="space-y-5">
                {review.issues.length > 0 && (
                  <div data-testid="checkout-issues" className="rounded-lg bg-amber-50 border border-amber-100 text-amber-800 text-sm p-3 space-y-1">
                    <div className="font-semibold inline-flex items-center gap-1"><AlertTriangle size={14} /> Please fix these before placing your order</div>
                    {review.issues.map((m) => <div key={m}>{m}</div>)}
                    <Link to="/cart" className="inline-block mt-1 font-semibold underline">Go to cart</Link>
                  </div>
                )}
                {review.vendors.map((g) => (
                  <div key={g.vendor_id} data-testid={`checkout-vendor-${g.vendor_slug || g.vendor_id}`}>
                    <div className="flex items-center justify-between text-sm mb-2"><span className="font-semibold text-stone-900">Sold by <span className="text-secondary">{g.vendor_name}</span></span><span className="text-stone-500">Vendor subtotal <b className="text-stone-900">{formatINR(g.subtotal)}</b></span></div>
                    <ul className="divide-y divide-border rounded-lg border border-border">
                      {g.items.map((i) => (
                        <li key={i.sku} className="flex items-center gap-3 p-3 text-sm" data-testid={`checkout-item-${i.sku}`}>
                          <img src={i.image} alt="" className="h-14 w-14 rounded-md object-cover bg-muted" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-stone-900 line-clamp-1">{i.product_name_snapshot}</div>
                            <div className="text-xs text-stone-500">{i.variant_name_snapshot && `${i.variant_name_snapshot} · `}{formatINR(i.unit_price)} × {i.quantity}</div>
                          </div>
                          <div className="font-semibold text-stone-900">{formatINR(i.subtotal)}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="rounded-xl border border-border bg-card p-6 h-fit lg:sticky lg:top-40" data-testid="checkout-summary">
          <h3 className="font-semibold text-stone-900 mb-4">Payment summary</h3>
          <div className="space-y-2 text-sm text-stone-600">
            <div className="flex justify-between"><span>Subtotal</span><span className="font-medium text-stone-800" data-testid="checkout-subtotal">{formatINR(review?.subtotal ?? 0)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span className="font-medium text-stone-800">{formatINR(review?.shipping_amount ?? 0)}</span></div>
            <div className="flex justify-between"><span>Tax</span><span className="font-medium text-stone-800">{formatINR(review?.tax_amount ?? 0)}</span></div>
            <div className="border-t border-border pt-3 mt-3 flex justify-between font-bold text-lg text-stone-900"><span>Total</span><span className="text-secondary" data-testid="checkout-total">{formatINR(review?.total_amount ?? 0)}</span></div>
          </div>
          <div className="mt-4 rounded-lg bg-amber-50 border border-amber-100 text-amber-800 text-xs p-3" data-testid="checkout-payment-note">
            <div className="font-semibold mb-1">Payment Gateway: <span data-testid="checkout-gateway-status">{payCfg?.status === "CONNECTED" ? payCfg.provider : "Coming Soon / Not Connected"}</span></div>
            <b>Payment: PENDING</b> — payment gateway is not yet integrated. Your order will be recorded with payment status <b>PENDING</b>; no money is collected now.
          </div>
          <button onClick={placeOrder} disabled={placing || !review || review.error || !review.ok} data-testid="place-order-btn"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3 hover:bg-[hsl(28_90%_38%)] disabled:opacity-50">
            {placing ? "Placing order..." : "Place Order"}
          </button>
          <p className="mt-3 text-[11px] text-stone-500">Prices, stock and totals are verified by Shri Vrindavan Shop servers when you place the order.</p>
        </aside>
      </div>
    </div>
  );
}
